import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { NAV } from "@/lib/nav";

const isActive = (group, loc) => group.items.some((i) => {
  const [path, query] = i.to.split("?");
  return loc.pathname === path || (path !== "/" && loc.pathname.startsWith(`${path}/`)) || (query && loc.search.includes(query));
});

const Group = ({ g, open, setOpen }) => {
  const loc = useLocation();
  const t = useRef();
  const ptr = useRef("");
  const enter = () => { clearTimeout(t.current); setOpen(g.key); };
  const leave = () => { t.current = setTimeout(() => setOpen((o) => (o === g.key ? null : o)), 150); };
  const cols = g.items.length > 5 ? "w-[460px] grid-cols-2" : "w-[300px] grid-cols-1";
  return (
    <div className="relative" onMouseEnter={enter} onMouseLeave={leave}>
      <button onPointerDown={(e) => { ptr.current = e.pointerType; }} onClick={() => setOpen(ptr.current === "mouse" ? g.key : open === g.key ? null : g.key)} aria-expanded={open === g.key} data-testid={`nav-group-${g.key}`}
        className={`flex items-center gap-1 whitespace-nowrap font-sub text-sm tracking-wide transition-colors duration-200 hover:text-terracotta ${isActive(g, loc) ? "text-terracotta" : "text-foreground/80"}`}>
        {g.label} <ChevronDown size={14} className={`transition-transform duration-200 ${open === g.key ? "rotate-180" : ""}`} />
      </button>
      {open === g.key && (
        <div className={`absolute left-1/2 top-full z-50 mt-3 -translate-x-1/2 border border-foreground/10 bg-shell/95 p-2 shadow-[0_24px_60px_-24px_rgba(16,43,63,0.45)] backdrop-blur-xl ${cols} grid gap-0.5`} data-testid={`nav-menu-${g.key}`}>
          {g.items.map((i) => (
            <Link key={i.to} to={i.to} onClick={() => setOpen(null)} data-testid={`nav-${i.id}`}
              className="block px-3 py-2.5 transition-colors duration-150 hover:bg-white">
              <span className="block font-sub text-sm">{i.label}</span>
              {i.desc && <span className="block text-xs text-muted-foreground">{i.desc}</span>}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export const DesktopNav = () => {
  const [open, setOpen] = useState(null);
  const loc = useLocation();
  useEffect(() => setOpen(null), [loc.pathname, loc.search]);
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && setOpen(null);
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, []);
  return <nav className="hidden items-center gap-5 min-[1380px]:flex 2xl:gap-7" aria-label="Hauptmenü">{NAV.map((g) => <Group key={g.key} g={g} open={open} setOpen={setOpen} />)}</nav>;
};

export const MobileNav = () => (
  <div className="mt-6 space-y-6">
    {NAV.map((g) => (
      <div key={g.key} data-testid={`nav-mobile-group-${g.key}`}>
        <p className="eyebrow text-terracotta">{g.label}</p>
        <div className={`mt-2 grid gap-x-4 ${g.items.length > 5 ? "grid-cols-2" : "grid-cols-1"}`}>
          {g.items.map((i) => <Link key={i.to} to={i.to} className="py-1.5 font-sub text-base" data-testid={`nav-${i.id}-mobile`}>{i.label}</Link>)}
        </div>
      </div>
    ))}
  </div>
);
